/**
 * Sentry PHI scrubbing — applied via `beforeSend` hooks in all 3 Sentry configs.
 *
 * Privacy Act 1988 APP 6/8/11 + Aged Care Act 2024 Chapter 9 Part 2 (protected
 * information — ss 537 onwards) + Privacy Act Part IIIC (Notifiable Data
 * Breaches scheme) prohibit transmission
 * of personal/health/sensitive information to third-party services without
 * meeting "reasonable steps" obligations. Sentry is hosted in the US which also
 * triggers APP 8 cross-border disclosure rules.
 *
 * Defence in depth strategy:
 * 1. `sendDefaultPii: false` in Sentry.init() — primary defence; stops the SDK
 *    from auto-attaching user/cookie/header data.
 * 2. Exclude clinical routes from instrumentation entirely (see `ignoreUrls`
 *    in each Sentry config).
 * 3. THIS FILE — recursive PHI scrubbing of any data that does end up in Sentry
 *    (regex-based identifiers + key-name-based field redaction).
 * 4. Audit log tables are NEVER routed to Sentry — they have a separate 7-year
 *    retention regime under Aged Care Act 2024 s 154 (record-keeping condition
 *    of registration) and the Aged Care Rules 2025.
 *
 * Limits: regex scrubbing is imperfect for free-text names embedded in stack
 * traces or template literals. The primary defence is keying clinical data
 * correctly so it can be redacted by field name.
 *
 * Audit history:
 *  - 2026-04-07: created during Phase 0.5 production-readiness remediation,
 *    expanded scope per legislative agent review.
 */

/** Identifiers that match a regex pattern anywhere in a string value. */
const PII_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  // Internal row identifiers can identify a care recipient when paired with route context.
  [/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, '[id]'],
  // Australian Individual Healthcare Identifier (16 digits starting 800360)
  [/\b800360\d{10}\b/g, '[IHI]'],
  // Medicare card number (10-11 digits, optional spaces). Must precede TFN regex.
  [/\b\d{4}\s?\d{5}\s?\d{1,2}\b/g, '[MEDICARE]'],
  // Centrelink CRN (9 digits + 1 alpha letter)
  [/\b\d{3}\s?\d{3}\s?\d{3}[A-Z]\b/g, '[CRN]'],
  // NDIS participant number (9 digits starting 43)
  [/\b43\d{7}\b/g, '[NDIS]'],
  // Aged Care MAC ID (AC + 8 digits)
  [/\bAC\d{8}\b/g, '[MACID]'],
  // Tax File Number (9 digits with TFN context — context-aware to avoid false positives on phone)
  [/(?<=tfn[^a-z0-9]*)\d{3}\s?\d{3}\s?\d{3}\b/gi, '[TFN]'],
  // Australian phone numbers (mobile + landline)
  [/\b(?:\+?61\s?|0)[2-478](?:[ -]?\d){8}\b/g, '[PHONE]'],
  // Email addresses
  [/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[EMAIL]'],
  // Date of birth patterns (DD/MM/YYYY or DD-MM-YYYY)
  [/\b(0?[1-9]|[12]\d|3[01])[/-](0?[1-9]|1[0-2])[/-]((19|20)\d{2})\b/g, '[DOB]'],
];

/**
 * Object keys whose values must be redacted regardless of content. Covers
 * "personal information", "sensitive information", and "health information"
 * categories under the Privacy Act + Aged Care Act 2024 protected info regime.
 */
// The previous regex was anchored with ^...$ which missed compound keys
// like `clientFirstName`, `patientName`, `residentMrn`, etc., and it was
// snake_case-only so camelCase variants (firstName / clientName / heartRate)
// used throughout the mobile + server-action payloads slipped through
// unredacted. The rule now matches both styles by accepting optional
// underscores AND allows the sensitive token to appear as a word-suffix
// on larger keys (e.g. `visitorFirstName`). Anchors are removed; each
// alternation is bounded to avoid greedy cross-matches.
const REDACTED_KEYS = new RegExp(
  '(^|_|(?<=[a-z0-9]))(' +
    // Personal identifiers — snake + camel + PascalCase suffixes
    '[Ff]irst_?[Nn]ame|[Ll]ast_?[Nn]ame|[Ff]ull_?[Nn]ame|given_?names|family_?name|' +
    '[Pp]referred_?[Nn]ame|[Cc]lient_?[Nn]ame|[Pp]atient_?[Nn]ame|' +
    '[Rr]esident_?[Nn]ame|[Ww]itness_?[Nn]ame|[Cc]ontact_?[Nn]ame|' +
    'dob|[Dd]ate_?[Oo]f_?[Bb]irth|' +
    // Free-text clinical content (both cases)
    'narrative|description|reason|note|notes|comments?|' +
    'immediate_?actions|immediateActions|root_?cause|rootCause|' +
    'corrective_?actions|correctiveActions|prevention_?plan|preventionPlan|' +
    'injury_?details|injuryDetails|injury_?description|injuryDescription|' +
    'wound_?description|woundDescription|' +
    // Address (in aged-care context, address+name identifies a vulnerable person)
    'address|street|suburb|postcode|state|location|' +
    // Clinical / health information
    'diagnosis|condition|medication|dose|prescription|allergy|allergies|' +
    'vitals|blood_?pressure|pulse|temperature|spo2|bgl|' +
    'respiratory_?rate|respiratoryRate|heart_?rate|heartRate|' +
    'systolic|diastolic|pain_?score|painScore|weight|' +
    'assessment|score|observation|observations|' +
    // Sensitive care decisions
    'goals_?of_?care|advance_?directive|nfr|dnr|resuscitation|' +
    'behaviour|bsp|restrictive_?practice|' +
    // Government identifiers
    // `ndis_participant_number` / `ndisParticipantNumber` must be listed
    // explicitly: `ndis_?number` does not match it, and the PII_PATTERNS entry
    // below only catches numbers beginning `43`. A participant number that does
    // not follow the issuer convention is accepted by the writer (it is a warn,
    // not a block), so field-name redaction is the only thing that catches it.
    // Longest alternatives first — the alternation is first-match.
    'ndis_?participant_?number|participant_?number|' +
    'ihi|medicare|medicare_?number|crn|tfn|ndis_?number|dva_?number|mac_?id|mrn|' +
    // Authentication credentials and bearer material
    'password|current_?password|new_?password|confirm_?password|' +
    'access_?token|refresh_?token|id_?token|authorization|api_?key|secret|' +
    // Financial / pension / Chapter 9 Part 2 protected information
    'pension|asset|income|bank|bsb|account|super_?fund|superannuation|member_?number' +
    ')(?=$|[A-Z_])',
  'i',
);

const REDACTED_VALUE = '[REDACTED]' as const;

/**
 * Controlled bypass for deliberately minimal operational telemetry from
 * PHI-heavy routes. Normal events from PHI_ROUTE_PATTERNS still drop; callers
 * must opt in and send only scrubbed tags/context, never request bodies.
 */
export const SAFE_SENTRY_CAPTURE_TAG = 'statura_safe_capture';

const NEXT_STATIC_CHUNK_RE = /(?:^|\/)_next\/static\/chunks\//;
const NEXT_WEBPACK_RUNTIME_RE = /(?:^|\/)_next\/static\/chunks\/webpack-[^/]+\.js(?:\?|$)/;
const NEXT_STALE_CHUNK_TEXT_RE =
  /ChunkLoadError|Loading chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed|Failed to find Server Action/i;
const NEXT_WEBPACK_UNDEFINED_CALL_RE =
  /Cannot read properties of undefined \(reading ['"]call['"]\)|undefined is not an object \(evaluating ['"][^'"]+\.call['"]\)/;
const WEBKIT_LOAD_FAILED_TEXT_RE = /^Load failed$/i;
const IOS_WEBKIT_CONTEXT_RE =
  /(?:\biOS\b|\biPhone\b|\biPad\b|\biPod\b|Chrome Mobile iOS|CriOS|Mobile Safari)/i;
const APP_CAPTURE_SOURCE_RE =
  /^(?:client_window_error|client_unhandled_rejection|next_error_boundary|react_query_error|react_mutation_error|server_action_result_error|roster_operational)$/;
// Canonical react-dom commit-phase DOM-mutation signatures across WebKit /
// Chromium / Firefox phrasings (removeChild / insertBefore on a node whose
// parent has already been detached). See isLikelyExternalDomMutationEvent.
const EXTERNAL_DOM_MUTATION_TEXT_RE =
  /not an object \(evaluating '[^']*\.(?:removeChild|insertBefore)'\)|Failed to execute '(?:removeChild|insertBefore)' on 'Node'|The node (?:to be removed|before which the new node is to be inserted) is no(?:t a| longer a) child of this node/i;
const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1', '[::1]']);
const LOOPBACK_URL_RE =
  /^https?:\/\/(?:localhost|127(?:\.\d{1,3}){3}|0\.0\.0\.0|\[::1\])(?::|\/|$)/i;

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value && typeof value === 'object' ? (value as UnknownRecord) : null;
}

function recordArray(value: unknown): UnknownRecord[] {
  if (!Array.isArray(value)) return [];
  return value.map(asRecord).filter((entry): entry is UnknownRecord => entry !== null);
}

function exceptionValues(event: UnknownRecord): UnknownRecord[] {
  const exception = asRecord(event.exception);
  return recordArray(exception?.values);
}

function exceptionFrames(exception: UnknownRecord): UnknownRecord[] {
  const stacktrace = asRecord(exception.stacktrace);
  return recordArray(stacktrace?.frames);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function frameLocation(frame: UnknownRecord): string {
  return [frame.filename, frame.abs_path, frame.module].map(stringValue).filter(Boolean).join(' ');
}

function nestedRecord(record: UnknownRecord, key: string): UnknownRecord | null {
  return asRecord(record[key]);
}

function recordString(record: UnknownRecord | null | undefined, key: string): string | undefined {
  return record ? stringValue(record[key]) : undefined;
}

function hasAppCaptureSource(event: UnknownRecord): boolean {
  const tags = asRecord(event.tags);
  const contexts = asRecord(event.contexts);
  const clientContext = contexts ? nestedRecord(contexts, 'statura_client_error') : null;

  const candidates = [
    recordString(tags, 'source'),
    recordString(clientContext, 'source'),
    contexts && nestedRecord(contexts, 'statura_server_action_error')
      ? 'server_action_result_error'
      : undefined,
    contexts && nestedRecord(contexts, 'statura_roster_operational')
      ? 'roster_operational'
      : undefined,
  ];

  return candidates.some((value) => typeof value === 'string' && APP_CAPTURE_SOURCE_RE.test(value));
}

function eventEnvironmentText(event: UnknownRecord): string {
  const tags = asRecord(event.tags);
  const contexts = asRecord(event.contexts);
  const browser = contexts ? nestedRecord(contexts, 'browser') : null;
  const os = contexts ? nestedRecord(contexts, 'os') : null;
  const device = contexts ? nestedRecord(contexts, 'device') : null;

  return [
    recordString(tags, 'browser'),
    recordString(tags, 'browser.name'),
    recordString(tags, 'os'),
    recordString(tags, 'device'),
    recordString(browser, 'name'),
    recordString(browser, 'version'),
    recordString(os, 'name'),
    recordString(os, 'version'),
    recordString(device, 'family'),
    recordString(device, 'model'),
  ]
    .filter(Boolean)
    .join(' ');
}

function hasOnlyFrameworkTransportFrames(frames: UnknownRecord[]): boolean {
  if (frames.length === 0) return true;
  return frames.every((frame) => {
    const location = frameLocation(frame);
    if (!location) return true;
    return NEXT_STATIC_CHUNK_RE.test(location) && !/(?:^|\/)app(?:\/|$)/.test(location);
  });
}

function isLikelyWebKitTransportLoadFailureEvent(
  event: UnknownRecord,
  exception: UnknownRecord,
): boolean {
  const type = stringValue(exception.type)?.trim();
  const value = stringValue(exception.value)?.trim();
  if (type !== 'TypeError' || !value || !WEBKIT_LOAD_FAILED_TEXT_RE.test(value)) return false;
  if (hasAppCaptureSource(event)) return false;
  if (!IOS_WEBKIT_CONTEXT_RE.test(eventEnvironmentText(event))) return false;

  return hasOnlyFrameworkTransportFrames(exceptionFrames(exception));
}

function isLoopbackUrl(value: string): boolean {
  if (!value) return false;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    return LOOPBACK_HOSTNAMES.has(url.hostname.toLowerCase()) || url.hostname.startsWith('127.');
  } catch {
    return LOOPBACK_URL_RE.test(value);
  }
}

/**
 * Drop localhost / loopback telemetry before it reaches production Sentry.
 *
 * Local `next start`, Playwright, and Momentic runs can inherit production
 * Sentry DSNs when exercising a production build. Those events are useful in
 * the terminal, but they must not page production incident channels.
 */
export function isLoopbackSentryEvent(event: unknown): boolean {
  const eventRecord = asRecord(event);
  if (!eventRecord) return false;

  const request = asRecord(eventRecord.request);
  const tags = asRecord(eventRecord.tags);
  const candidates = [request?.url, eventRecord.transaction, tags?.url].filter(
    (value): value is string => typeof value === 'string',
  );
  return candidates.some(isLoopbackUrl);
}

/**
 * Detect stale Next.js static asset / deploy-boundary noise.
 *
 * Vercel can briefly serve an HTML/RSC shell from one deployment while the
 * browser requests static chunks from another. Next's webpack runtime then
 * reports low-level errors such as `undefined.call` or `ChunkLoadError`.
 * iOS/WebKit can also surface an interrupted app-router transport request as a
 * bare `TypeError: Load failed` with no Statura capture tags. These are not
 * application defects and are self-healed by a reload. Keep the predicate narrow
 * so real app TypeErrors still reach Sentry.
 */
export function isLikelyNextStaticAssetDriftEvent(event: unknown): boolean {
  const eventRecord = asRecord(event);
  if (!eventRecord) return false;

  for (const exception of exceptionValues(eventRecord)) {
    const text = [exception.type, exception.value].map(stringValue).filter(Boolean).join(' ');
    const frames = exceptionFrames(exception);
    const frameText = frames.map(frameLocation).join(' ');
    const combined = `${text} ${frameText}`;

    const hasNextStaticChunkReference = NEXT_STATIC_CHUNK_RE.test(combined);
    const hasWebpackRuntimeFrame = frames.some((frame) =>
      NEXT_WEBPACK_RUNTIME_RE.test(frameLocation(frame)),
    );
    const isKnownChunkLoadFailure = NEXT_STALE_CHUNK_TEXT_RE.test(text);
    const isWebpackUndefinedCall = NEXT_WEBPACK_UNDEFINED_CALL_RE.test(text);

    if (isKnownChunkLoadFailure && hasNextStaticChunkReference) return true;
    if (/Failed to find Server Action/i.test(text)) return true;
    if (isWebpackUndefinedCall && hasWebpackRuntimeFrame) return true;
    if (isLikelyWebKitTransportLoadFailureEvent(eventRecord, exception)) return true;
  }

  return false;
}

/**
 * Browser extensions and in-browser page translators (Google Translate, Safari
 * Translate, Samsung Internet) mutate the DOM that React owns by wrapping or
 * swapping text nodes. React's next commit can then call removeChild or
 * insertBefore on a node whose parent is already detached, throwing one of
 * these signatures from deep inside react-dom with no application frame.
 *
 * Keep this predicate narrow: only canonical detached-node messages are
 * suppressed, while genuine application TypeErrors continue to reach Sentry.
 */
export function isLikelyExternalDomMutationEvent(event: unknown): boolean {
  const eventRecord = asRecord(event);
  if (!eventRecord) return false;

  for (const exception of exceptionValues(eventRecord)) {
    const text = [exception.type, exception.value].map(stringValue).filter(Boolean).join(' ');
    if (EXTERNAL_DOM_MUTATION_TEXT_RE.test(text)) return true;
  }

  return false;
}

/** Apply regex-based scrubbing to a single string. */
export function scrubPHIString(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = text;
  for (const [re, rep] of PII_PATTERNS) {
    out = out.replace(re, rep);
  }
  return out;
}

/** Recursively scrub PHI from any value (string, array, object). Depth-limited to 5. */
export function scrubPHI(value: unknown, depth = 0): unknown {
  if (depth > 5 || value == null) return value;
  if (typeof value === 'string') return scrubPHIString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (Array.isArray(value)) return value.map((v) => scrubPHI(v, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (REDACTED_KEYS.test(key)) {
        out[key] = REDACTED_VALUE;
      } else {
        out[key] = scrubPHI(v, depth + 1);
      }
    }
    return out;
  }
  return value;
}

/**
 * Sentry beforeSend hook: scrub PHI from every part of an event before it
 * leaves the process. Used by all 3 Sentry configs (client, server, edge).
 *
 * Typed as `unknown` then cast internally because Sentry's `ErrorEvent` type
 * has many optional fields and we mutate in place. The return value preserves
 * the original event reference (with mutations applied), satisfying Sentry's
 * `(event) => event | null` contract.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function scrubSentryEvent<T extends Record<string, any>>(event: T): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = event as any;
  if (e.request?.url) e.request.url = scrubPHIString(e.request.url);
  if (e.request?.data) e.request.data = scrubPHI(e.request.data);
  if (e.request?.headers) e.request.headers = scrubPHI(e.request.headers);
  if (e.request?.cookies) e.request.cookies = '[REDACTED]';
  if (e.extra) e.extra = scrubPHI(e.extra);
  if (e.contexts) e.contexts = scrubPHI(e.contexts);
  if (e.tags) e.tags = scrubPHI(e.tags);
  if (e.user) {
    // Keep id for correlation, drop everything else
    e.user = e.user.id ? { id: e.user.id } : undefined;
  }
  if (e.message) e.message = scrubPHIString(e.message);
  if (e.exception?.values) {
    for (const ex of e.exception.values) {
      if (ex?.value) ex.value = scrubPHIString(ex.value);
    }
  }
  if (e.breadcrumbs) {
    e.breadcrumbs = (e.breadcrumbs as Array<Record<string, unknown>>).map((b) => ({
      ...b,
      message: typeof b.message === 'string' ? scrubPHIString(b.message) : b.message,
      data: scrubPHI(b.data),
    }));
  }
  return event;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isSafeSentryCapture(event: Record<string, any>): boolean {
  return event.tags?.[SAFE_SENTRY_CAPTURE_TAG] === 'true';
}

/**
 * Keep deliberately safe operational captures from PHI-heavy routes while
 * removing route URL, request payload, breadcrumbs, and user context.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function scrubSafeSentryPhiEvent<T extends Record<string, any>>(event: T): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = event as any;
  const routeClass =
    typeof e.tags?.route_class === 'string' ? scrubPHIString(e.tags.route_class) : 'redacted';
  e.request = { url: `app://safe-operational-error${routeClass}` };
  e.user = undefined;
  e.breadcrumbs = [];
  if (e.extra) e.extra = scrubPHI(e.extra);
  if (e.contexts) e.contexts = scrubPHI(e.contexts);
  if (e.tags) e.tags = scrubPHI(e.tags);
  return event;
}

/**
 * Sentry beforeSendTransaction hook: transactions do not flow through
 * beforeSend, so trace descriptions/tags need their own PHI scrub pass.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function scrubSentryTransaction<T extends Record<string, any>>(transaction: T): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tx = transaction as any;
  if (tx.transaction) tx.transaction = scrubPHIString(tx.transaction);
  if (tx.request?.url) tx.request.url = scrubPHIString(tx.request.url);
  if (tx.contexts) tx.contexts = scrubPHI(tx.contexts);
  if (tx.tags) tx.tags = scrubPHI(tx.tags);
  if (tx.data) tx.data = scrubPHI(tx.data);
  if (Array.isArray(tx.spans)) {
    tx.spans = tx.spans.map((span: Record<string, unknown>) => ({
      ...span,
      description:
        typeof span.description === 'string' ? scrubPHIString(span.description) : span.description,
      data: scrubPHI(span.data),
      tags: scrubPHI(span.tags),
    }));
  }
  return transaction;
}

/**
 * URL paths that should NEVER be instrumented by Sentry. Even with PHI
 * scrubbing, an error stack trace from a clinical route may contain PHI in
 * template literals or JSX rendering. Excluding these routes is the strongest
 * defence.
 */
export const PHI_ROUTE_PATTERNS = [
  // Phase 3 Xero payroll bridge — protects OAuth callback (which carries
  // auth code and token-bearing redirect) and Xero-specific routes from
  // accidental Sentry capture of token fragments. Spec §5.6 + prior audit P2-B.
  /\/api\/xero(\/|$)/,
  /\/api\/payroll\/xero(\/|$)/,
  // Phase 4 AR bridge — cron + fallback CSV routes carry invoice metadata
  // (client name in CSV, line item descriptions) that we treat as PHI-adjacent.
  /\/api\/cron\/xero-ar-(push|payment-sync|recon-sweep)(\/|$)/,
  /\/api\/finance\/invoices\/[^/]+\/fallback-csv(\/|$)/,
  // Post-build hardening P0/P1 patch — broad route classes missed by the
  // previous deny-list. These routes render named care recipients, family
  // portal records, home-care services/statements, residential clinical
  // workflows, payroll/staff records, monitoring telemetry, telehealth
  // records, data breach registers, and service-role cron sweeps.
  /\/family(\/|$)/,
  /\/clients(\/|$)/,
  /\/residential(\/|$)/,
  /\/advance-care-plans(\/|$)/,
  /\/case-notes(\/|$)/,
  /\/care-notes(\/|$)/,
  /export-care-notes-period/,
  /care-note-period-export/,
  /\/home-care(\/|$)/,
  /\/payroll(\/|$)/,
  /\/telehealth(\/|$)/,
  /\/monitoring(\/|$)/,
  /\/staff\/[^/]+(\/|$)/,
  /\/compliance\/data-breach(\/|$)/,
  /\/api\/cron(\/|$)/,
  /\/clinical(\/|$)/,
  /\/medications(\/|$)/,
  /\/assessments(\/|$)/,
  /\/care-plans(\/|$)/,
  /\/observations(\/|$)/,
  /\/wounds(\/|$)/,
  /\/residents\/[^/]+\/notes/,
  /\/clients\/[^/]+\/notes/,
  // Phase 1.5 P3-F — invoice line items + claim descriptions can contain
  // PHI (client names, service descriptions). Exclude finance/claims
  // routes from Sentry instrumentation.
  /\/finance(\/|$)/,
  /\/claims(\/|$)/,
  // Services Australia HSDP NOI drafts construct agency-bound payloads with
  // names, DOB, MAC IDs, and addresses. Keep the route out of Sentry even
  // though the integration audit log stores hashes only.
  /\/integrations\/services-australia(\/|$)/,
  /\/compliance\/services-australia(\/|$)/,
  // Phase 2 e-signature public signing — the /sign/[token] URL carries
  // the raw signing token in the path. Token is unrecoverable from the
  // DB (only SHA-256 stored) but must NOT land in Sentry breadcrumbs or
  // error frames. Also exclude the API route the signer's browser POSTs
  // back to. Per spec §13: "Public signing routes must have IP rate
  // limits and structured abuse logs."
  /\/sign(\/|$)/,
  /\/api\/sign(\/|$)/,
  // Phase 2.6 P1-C — leave_requests.reason can carry sick-leave medical info;
  // worker_availability blocks may include personal context.
  /\/me\/leave(\/|$)/,
  /\/roster\/leave(\/|$)/,
  /\/availability(\/|$)/,
  // R4-4b — worker self-service surfaces. /me/preferences gathers SCHADS
  // pref data (gender match, willingness, suburbs) which can carry personal
  // context; /me/renewals carries certification + screening identifiers;
  // /me/hours renders projected pay (financial PII).
  /\/me\/preferences(\/|$)/,
  /\/me\/renewals(\/|$)/,
  /\/me\/hours(\/|$)/,
  // R4-4c — proposal apply UIs surface client preferred-names + restrictive
  // practice types (Aged Care Act Chapter 9 Part 2 protected info).
  /\/roster\/proposals(\/|$)/,
  // R4-4d C-30 + C-41 + C-44 — funder mix exposes per-class resident counts +
  // financial projections; care minutes audit pack PDF endpoint surfaces
  // facility + tenant labels alongside ACQSC compliance evidence; BSP stale
  // surface lists client preferred-names alongside RP practice types.
  /\/dashboards\/finance\/funder-mix(\/|$)/,
  /\/api\/audit\/care-minutes(\/|$)/,
  /\/roster\/health\/bsp-stale(\/|$)/,
  // R4-4e B-24 — travel three-option picker surfaces client home_lat/lng
  // (geolocation = sensitive personal info under Privacy Act APP 11).
  /\/roster\/optimise-day(\/|$)/,
  // Phase 2.9 P1-C — performance/supervision narratives carry sensitive worker
  // PII + manager comments + reflective practice notes.
  /\/me\/performance(\/|$)/,
  /\/hr\/performance(\/|$)/,
  /\/hr\/clinical-supervision(\/|$)/,
  // Phase 2.7 — training records may include accreditation numbers; quiz
  // attempts surface assessment narratives; certificate metadata is sensitive.
  // The /me/training hub loads worker training records + certificate metadata.
  /\/staff\/training(\/|$)/,
  /\/me\/courses(\/|$)/,
  /\/me\/training(\/|$)/,
  // Phase 2.10 — screening clearance numbers (NDIS, AHPRA, police) are
  // protected information; EBA agreements include rate-line PII.
  /\/compliance\/screening(\/|$)/,
  /\/api\/integrations\/fit2work(\/|$)/,
  // R7-D2 provider obligations surface lists facility-level compliance
  // counts that include admission disclosure breaches.
  /\/compliance\/provider-obligations(\/|$)/,
  /\/compliance\/ndis-overlay(\/|$)/,
  /\/staff\/compliance(\/|$)/,
  /\/hr\/eba(\/|$)/,
  /\/settings\/payroll\/eba(\/|$)/,
  // Phase 2.8 — onboarding documents carry TFN/super/bank/police-check/
  // NDIS-screening/VEVO grant numbers. Casual conversion notices contain
  // worker context tied to s.66AAB written-notice integrity.
  /\/me\/onboarding(\/|$)/,
  /\/me\/casual-conversion(\/|$)/,
  /\/hr\/onboarding(\/|$)/,
  /\/hr\/casual-conversion(\/|$)/,
  // Phase 4.2 P1-D — whistleblower disclosures contain free-text
  // allegations + named-person identities. Aged Care Act 2024 s 165
  // protected information; Corporations Act 2001 s 1317AAE confidentiality
  // of identity; PIDA 2013 s 20 confidentiality offence. Public POST entry
  // is also at /whistleblower (no auth) — exclude entire surface.
  /\/whistleblower(\/|$)/,
  /\/api\/whistleblower(\/|$)/,
  /\/compliance\/whistleblower(\/|$)/,
  /\/api\/compliance\/whistleblower(\/|$)/,
  // Phase 4.1 P1-C — incident free-text + clinical narrative (immediate
  // actions, root cause, injury detail) carries PHI even when scrubbed.
  /\/compliance\/incidents(\/|$)/,
  // Phase 4.3 P1-H — complaint description, communication content, and
  // free-text categories carry resident names + clinical narrative.
  // Family-portal POST surface mirrors the deny-list.
  /\/complaints(\/|$)/,
  /\/api\/portal\/feedback(\/|$)/,
  // Phase 4.7 P1-Q — code-of-conduct breach descriptions contain alleged
  // misconduct + identifying narrative + investigation notes.
  /\/conduct(\/|$)/,
  /\/compliance\/conduct(\/|$)/,
  /\/compliance\/code-of-conduct(\/|$)/,
  // Phase 4.8 P1-Q — governance / risk / COI declarations include
  // identifying context covered by ACA 2024 Chapter 9 Part 2 protected
  // information regime.
  /\/governance(\/|$)/,
  /\/compliance\/governance(\/|$)/,
  // Phase 4.9 P1-D — restrictive practice description, justification,
  // alternatives, monitoring notes, episode reasons all carry PHI.
  /\/compliance\/restrictive-practices(\/|$)/,
  // Phase 4 Wave 6 — IPC rosters and outbreak lists carry named residents,
  // infection status, isolation state, and exposure/contact-tracing detail.
  /\/compliance\/ipc(\/|$)/,
  // Phase 4.4 — quality indicator submissions contain narrative columns
  // (improvement actions, governance review notes) tied to consumer cohort.
  /\/compliance\/quality(\/|$)/,
  // Phase 4.10 P3-A — open-disclosure summary, decline-reason,
  // attendees-by-name. Free-text apology / explanation surface.
  /\/compliance\/open-disclosure(\/|$)/,
  // Phase 7.4 P1-C — admin pages render cross-tenant data (organisations,
  // staff names + emails, notification subjects, tenant-keyed deliveries,
  // pending account-deletion requests). Even with PHI scrubbing, error
  // stack traces from admin routes can carry cross-tenant identifiers.
  // Exclude all (admin) routes from instrumentation.
  /\/admin(\/|$)/,
  // Phase 8.7 P2-E — customer-legal acceptance route includes IP, UA,
  // accepting admin's email, document descriptors. The cluster is PII
  // for the admin and evidentiary for contract formation. Sentry has no
  // legitimate reason to ingest this traffic; exclude from instrumentation.
  /\/api\/customer-legal(\/|$)/,
  // Phase 8 Group E — free-text assistance routes can carry resident names
  // + clinical narrative. /api/help/chat stays local but still receives
  // user-authored help questions; /api/translate proxies free text to
  // Anthropic Claude. Sentry events from these routes can include request
  // bodies, response bodies, or prompt-construction errors that leak PHI
  // even after string-level scrubbing. Drop the entire assistance traffic
  // class from Sentry.
  /\/api\/help\/chat(\/|$)/,
  /\/api\/translate(\/|$)/,
  // Phase 6.6 P1-A — reporting framework runs cross-cluster SQL with
  // resident / staff / medication / claim / incident PHI in the result
  // set. Catalog SQL renders staff names (`first_name || ' ' || last_name`),
  // client identifiers, billing detail. Even with string-level scrubbing,
  // an error stack from /reports or /api/reports can carry the parameters
  // map (filter values include client_id, staff_id, date ranges) and the
  // definition slug. The cluster also drives PHI-flagged CSV/XLSX storage
  // artefacts. Drop the whole reporting traffic class from Sentry.
  /\/reports(\/|$)/,
  /\/api\/reports(\/|$)/,
  /\/compliance\/reporting(\/|$)/,
  // R2 P3-I — Phase A rostering surfaces. /audit/evidence renders client names
  // + claim descriptions + AN-ACC class metadata; /roster/replace shows ranked
  // worker lists + visit context; /roster/timesheets/reconciliation surfaces
  // visit/round assignment lists with client + worker names.
  /\/audit\/evidence(\/|$)/,
  /\/roster\/replace(\/|$)/,
  /\/roster\/timesheets\/reconciliation(\/|$)/,
  // Round 5 Tab C UIA-P2-498 — /analytics/facilities/[id] renders client
  // first/last names + AN-ACC classification_level + staff names; /analytics/
  // predictions renders client labels alongside budget alerts. /analytics/
  // board-pack aggregates SIRS P1/P2 counts + workforce identifiers. The
  // cluster falls under Aged Care Act 2024 Chapter 9 Part 2 protected
  // information; exclude the whole analytics surface from Sentry.
  /\/analytics(\/|$)/,
  // Round 5 Tab D UIA-P1-518 — /help/tickets renders user-authored ticket
  // subject + description + ticket_messages content + audit_logs.changes
  // JSON. Free-text ticket bodies regularly carry resident names ("Mrs Smith
  // is missing her morning MAR"), clinical context, billing dispute detail,
  // and staff personal information. The original ticket pages were not
  // previously in PHI_ROUTE_PATTERNS — adding here so Sentry events
  // from the entire /help/tickets surface drop in beforeSend before payload
  // leaves the process. Privacy Act 1988 APP 6/11 + Aged Care Act 2024
  // Chapter 7 (protected information).
  /\/help\/tickets(\/|$)/,
  // UIA-P3-361 closeout — compliance defence-in-depth gap. /compliance/
  // clinical-governance aggregates medication_incidents.description,
  // restrictive_practices.description/justification, and incidents.
  // open_disclosure_summary (all PHI-bearing free text). /compliance/audit
  // renders audit_logs.changes JSONB diffs that may carry client/staff
  // names. /compliance/capability-framework + /certifications + /registration
  // render staff identifiers + screening/certification numbers (Privacy Act
  // 1988 APP 6/11). /compliance/policies surfaces correspondence references
  // + policy-acknowledgement audit chains. Exclude the whole surface from
  // Sentry instrumentation so error stacks from these dashboards drop in
  // beforeSend before the payload leaves the process.
  /\/compliance\/clinical-governance(\/|$)/,
  /\/compliance\/audit(\/|$)/,
  /\/compliance\/capability-framework(\/|$)/,
  /\/compliance\/certifications(\/|$)/,
  /\/compliance\/registration(\/|$)/,
  /\/compliance\/policies(\/|$)/,
  // UIA-P3-457 closeout — /compliance/documents surface (expiring list +
  // any future child routes). User-uploaded filenames can embed resident
  // names ("John Smith ACP signed.pdf"). The page render is sanitised to a
  // PHI-free reference token, but the underlying `documents.fileName` is
  // still loaded over the wire — if a 500 fires while constructing the
  // table, the error stack carries the filename array. Drop the whole
  // /compliance/documents class from Sentry to close the defence-in-depth
  // gap. Privacy Act 1988 APP 11 + Aged Care Act 2024 Chapter 7.
  /\/compliance\/documents(\/|$)/,
  // G39 (UX Coverage Audit Round 1, 2026-05-18) — roster forecast +
  // optimiser surfaces both render `breakdown.shifts[].breakdown` JSONB
  // payloads that include classification (PII), per-worker financial
  // figures, and shift-by-shift wage components for every worker on a
  // roster. The /payroll(/|$) deny-list catches /payroll/* but not the
  // roster surfaces. Adding the two routes closes the gap so error
  // stacks from these dashboards drop in beforeSend before payload
  // leaves the process. Privacy Act 1988 APP 11 + Aged Care Act 2024
  // Chapter 7.
  /\/roster\/forecast(\/|$)/,
  /\/roster\/optimiser(\/|$)/,
];
